// Cleaned and optimized version of your AuditLocation component

import React, { useEffect, useState, useMemo, useCallback, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Input,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Modal,
  Form,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import * as Yup from "yup";
import { useFormik } from "formik";
import { showSuccessToast, showErrorToast } from "../../helpers/toast_helper";
import { createSelector } from "reselect";
import classnames from "classnames";

import BreadCrumb from "Components/Common/BreadCrumb";
import DeleteModal from "Components/Common/DeleteModal";
import Loader from "Components/Common/Loader";
import UserForm from "./UserForm";
import TableContainer from "./TableContainer";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import type { RootState } from "store";

import { fetchUserData, deleteMultiUser, editUser } from "slices/User/thunk";
import UserDetailpage from "./UserDetailPage";
import { exportToExcel } from "helpers/exportToExcel";
import { fetchUserApi } from "services/userService";
import { usePermissions } from "../../hooks/usePermissions";
import { PermissionEnum } from "../../Components/constants/permissions";
import { clearUserMessages } from "slices/User/reducer";

const UserRecords = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const permissions = usePermissions();

  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deleteModal, setDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number>(0)
  const [selectedUserStatus, setSelectedUserStatus] = useState("");
  const [selectedCheckBox, setSelectedCheckBox] = useState<boolean>(false);
  const [rowID, setRowId] = useState<string | null>('');
  const [tabStatus, setTabStatus] = useState<string>("");
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>([]);
  const selectLayoutState = (state: any) => state;
  const userData = createSelector(selectLayoutState, (state) => ({
    UserData1: state.User.UserData,
    SingleUserData: state.User.SingleUserData,
    deleteUserSuccessfull: state.User.deleteUserSuccessfull,
    editUserSuccefull: state.User.editUserSuccessfull,
    userSuccess: state.User.userSuccessMsg,
    deleteSuccess: state.User.deleteSuccessMsg,
    userError: state.User.userErrorMsg,
    userChangePasswordSuccess: state.ChangePassword.changePwdSuccessMsg,
    userChangePasswordError: state.ChangePassword.changePasswordError,
    loading: state.User.loading,
    paginationCount: state.User.paginationCount,
    pagination: state.User.pagination,
  }));



  const { UserData1, userError, deleteSuccess, paginationCount, deleteUserSuccessfull, editUserSuccefull, userChangePasswordError, userChangePasswordSuccess, SingleUserData, userSuccess, loading, pagination } = useSelector(userData);


  const UserData = UserData1?.users;
  const singleUserData = SingleUserData?.users?.[0];
  const CountData = paginationCount;
  const selectLoginState = (state: RootState) => state.Login;

  const selectUserData = createSelector(selectLoginState, (login) => ({
    user: login.user,
    error: login.error,
    useraccessrights: login.useraccessrights
  }));

  const { user, useraccessrights } = useSelector(selectUserData);

  const endpoint = window.location.pathname;

  const moduleAccessRights = useraccessrights.accessrights.find(
    (item: { iseditaccess: boolean; path: string, viewonly: string, moduleaccess: string }) => item.path === endpoint
  ) as { path: string; iseditaccess: boolean, viewonly: boolean } | undefined;


  const viewOnlyAccess = moduleAccessRights?.viewonly ?? false

 const handleExportUsers = async () => {
  if (!pagination?.total_pages) {
    showErrorToast("Pagination data missing!");
    return;
  }

  showSuccessToast("Preparing full export...");

  let allUsers: any[] = [];

  // ✅ Loop through all pages & fetch each page directly from API
  for (let p = 1; p <= pagination.total_pages; p++) {
    const payload = {
      user_id: 0,
      phone_no: "",
      status: "",
      from_date: "",
      to_date: "",
      search_keyword: searchQuery,
      is_verified: true,
      page: p,
      page_size: limit, // current size (10)
    };

    const response = await fetchUserApi(payload);
    if (response.success) {
      allUsers = [...allUsers, ...response.data.users];
    }
  }

  if (allUsers.length === 0) {
    showErrorToast("No data available to export!");
    return;
  }

  exportToExcel(allUsers, "User_Report");
  showSuccessToast("User report exported successfully!");
};


  const [filteredUserData, setFilteredUserData] =
    useState<any>([]);

  useEffect(() => {
    setFilteredUserData(UserData || []);
  }, [UserData]);

  // Utility: Convert date to UTC ISO string with optional end-of-day time
  const toUtcISOStringRange = (date: Date, isEnd: boolean = false): string => {
    const offset = date.getTimezoneOffset(); // in minutes
    const utcDate = new Date(date.getTime() - offset * 60000);
    utcDate.setHours(isEnd ? 23 : 0, isEnd ? 59 : 0, isEnd ? 59 : 0, isEnd ? 999 : 0);
    return utcDate.toISOString();
  };

  // Map tab value to status key
  const tabMap: Record<string, string> = {
    "1": "All",
    "2": "Active",
    "3": "Inactive",
    "4": "Download"
  };

  // useEffect: Show success/error toasts
  useEffect(() => {
    // whenever tab changes, reset page to 1
    setPage(1);
  }, [tabStatus]);

  // Handle refresh after delete/edit operations (separate from main fetch)
  useEffect(() => {
    if (deleteUserSuccessfull || editUserSuccefull) {
      const alteredStatus =
        tabStatus.toLowerCase() === "inactive" ? "Inactive" : tabStatus;

      dispatch(
        fetchUserData({
          user_id: 0,
          phone_no: "",
          status: alteredStatus,
          from_date: "",
          to_date: "",
          search_keyword: searchQuery,
          is_verified: true,
          page: page,
          page_size: limit,
        })
      );
    }
  }, [deleteUserSuccessfull, editUserSuccefull, dispatch]);

  // Main useEffect for fetching user data - only on filter changes
  useEffect(() => {
    // Skip if rowID is set (single user view handled separately)
    if (rowID) {
      const payload = {
        user_id: rowID,
        phone_no: "",
        status: "",
        from_date: "",
        to_date: "",
        search_keyword: "",
        is_verified: true,
        page,
        page_size: limit,
      };
      dispatch(fetchUserData(payload));
      return;
    }

    // Main list view with debounce
    const handler = setTimeout(() => {
      const alteredStatus =
        tabStatus.toLowerCase() === "inactive" ? "Inactive" : tabStatus;

      dispatch(
        fetchUserData({
          user_id: 0,
          phone_no: "",
          status: alteredStatus,
          from_date: "",
          to_date: "",
          search_keyword: searchQuery,
          is_verified: true,
          page: page,
          page_size: limit,
        })
      );
    }, 500);

    return () => clearTimeout(handler); // cleanup 
  }, [rowID, searchQuery, page, limit, tabStatus, dispatch]);


  useEffect(() => {
  let triggered = false;

  if (userSuccess?.message) {
    showSuccessToast(userSuccess.message);
    triggered = true;
  }

  if (deleteSuccess?.message) {
    showSuccessToast(deleteSuccess.message);
    triggered = true;
  }

  if (userChangePasswordSuccess) {
    showSuccessToast(userChangePasswordSuccess);
    triggered = true;
  }

  if (userChangePasswordError) {
    showErrorToast(userChangePasswordError);
    triggered = true;
  }

  if (userError) {
    showErrorToast(userError);
    triggered = true;
  }

  // Only clear messages once per cycle
  if (triggered) {
    dispatch(clearUserMessages());
  }
}, [
  userSuccess?.id,
  deleteSuccess?.id,
  userChangePasswordSuccess,
  userChangePasswordError,
  userError
]);



  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      country_code: singleUserData?.country_code || "",
      email: singleUserData?.email || "",
      user_id: singleUserData?.id,
      first_name: singleUserData?.first_name || "",
      last_name: singleUserData?.last_name || "",
      profile_pic_base64: singleUserData?.profile_pic_base64 || "",
      isverified: singleUserData?.isverified.toLowerCase() == "verified" ? true : false,
      workertypeId: singleUserData?.workertypeId || 2,
      phone_no: singleUserData?.phone_no || "",
      dial_code: singleUserData?.dial_code || "+91",
      gender: singleUserData?.gender || "",
      dob: singleUserData?.dob || "",
      cpr_id: singleUserData?.cpr_id || "",
      per_address: singleUserData?.per_address || "",
      curr_address: singleUserData?.curr_address || "",
      marital_status: singleUserData?.marital_status || "",
      occupation: singleUserData?.occupation || "",
      // workertypename: singleUserData?.workertypename || "",
    },
    validationSchema: Yup.object({


      email: Yup.string()
        .trim()
        .required("Please enter email")
        .email("Invalid email format"),

     
      marital_status: Yup.string()
        .trim()
        .required("Please select marital status"),


      dial_code: Yup.string().trim().required("Please select country code"),

     gender: Yup.string()
        .trim()
        .oneOf(["Male", "Female", "Other"], "Please select a gender")
        .required("Please select gender"),


      dob: Yup.date()
        .required("Date of birth is required")
        .nullable()
        .max(new Date(), "DOB cannot be in the future"),
      occupation: Yup.string()
        .trim()
        .required("Please enter occupation")
        .nullable()
        .matches(/^[A-Za-z\s]*$/, "Occupation must only contain letters and spaces."),
      per_address: Yup.string()
        .trim()
        .required("Please enter permanant address")
        .nullable()
        .matches(/^[A-Za-z0-9\s,./-]*$/, "Address can only contain letters, numbers, spaces, commas, periods, slashes, and hyphens."),

      curr_address: Yup.string()
        .trim()
        .required("Please enter current address")
        .nullable()
        .matches(/^[A-Za-z0-9\s,./-]*$/, "Address can only contain letters, numbers, spaces, commas, periods, slashes, and hyphens."),

      cpr_id: Yup.string()
        .trim()
        .nullable()
        .matches(/^[0-9]{9}$/, "Please enter a valid CPR ID (9 digits only).")
        .test("is-numeric", "CPR ID must contain only numbers (no alphabets or special characters).", (value) => {
          if (value) {
            return /^[0-9]{9}$/.test(value);
          }
          return true;
        }),


    }),

    onSubmit: (values, { resetForm }) => {
      const updatedUser = {
        ...values,
        updated_at: new Date().toISOString(),

      };

      if (isEdit) {
        dispatch(editUser(updatedUser));
      } else {
        resetForm();
      }

      toggle();
    },
  });






  const toggle = useCallback(() => {
    if (modal) {
      // Only reset state when closing
      setIsEdit(false);
    }
    setModal(!modal);
  }, [modal]);

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleUserClick = useCallback((id: number) => {

    const payload = {
      "user_id": id,
      "phone_no": "",
      "status": "",
      "from_date": "",
      "to_date": "",
      "search_keyword": "",
      "is_verified": true,
      "page": page,
      "page_size": limit

    }
    dispatch(fetchUserData(payload))

    setIsEdit(true);
    toggle();
  }, [toggle]);


  const handleUserDelete = () => {

    dispatch(fetchUserData({
      "user_id": 0,
      "phone_no": "",
      "status": "",
      "from_date": "",
      "search_keyword": "",
      "to_date": "",
      "is_verified": true,
      "page": page,
      "page_size": 10
    }))
    setDeleteModal(false);
  };

  // const handleClearFilter = () => {
  //   setSelectedDateRange([]); 
  // }


  const deleteCheckbox = () => {

    let payload: { user_id: number[]; status: string };

    if (selectedCheckBox) {

      const checkedElements: NodeListOf<HTMLInputElement> = document.querySelectorAll(".leadsCheckBox:checked");

      const selectedIds = Array.from(checkedElements).map((el) => {
        const id = el.getAttribute("data-id");
        return id ? Number(id) : null;
      }).filter((id): id is number => id !== null);

      const enableButton = selectedIds.length > 0;

      setIsMultiDeleteButton(enableButton);

      let payloadStatus = String(selectedUserStatus)?.toLowerCase() === "active" ? "Inactive" : "Active";

      if (selectedIds.length > 0) {

        // Multi-user delete
        payload = {
          user_id: selectedIds,
          status: payloadStatus,
        };
        dispatch(deleteMultiUser(payload));

      }
      setSelectedCheckBox(false);
      setIsMultiDeleteButton(false);

      // } else if (selectedUserId !== null && selectedUserId !== undefined && deleteModal) {
    } else {


      payload = {
        user_id: [selectedUserId],
        status: selectedUserStatus.toLowerCase() === "active" ? "Inactive" : "Active", // Presumably already correct
      };
      dispatch(deleteMultiUser(payload));

    }



    setDeleteModal(false);

  };



  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setPage(1);

  };

  const handleValidDate = (date: any) => {
    const date1 = moment(new Date(date)).format("DD MMM Y");
    return date1;
  };
  const handleValidTime = (time: any) => {
    const time1 = new Date(time);
    const getHour = time1.getHours();
    const getMin = time1.getMinutes();
    const getTime = `${getHour}:${getMin}`;
    var meridiem = "";
    if (getHour >= 12) {
      meridiem = "PM";
    } else {
      meridiem = "AM";
    }
    const updateTime =
      moment(getTime, "hh:mm").format("hh:mm") + " " + meridiem;
    return updateTime;
  };




  // Checked All
  const checkedAll = useCallback(() => {

    const checkall: any = document.getElementById("checkBoxAll");

    const ele = document.querySelectorAll(".leadsCheckBox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
        setIsMultiDeleteButton(true);
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
        setIsMultiDeleteButton(false);
      });
    }
    // selectGuids();
    // deleteCheckbox();
  }, []);

  // check single
  const singleCheck = useCallback(() => {
    setSelectedCheckBox(true)
    const checkAll = document.getElementById("checkBoxAll") as HTMLInputElement | null;
    const elements = document.querySelectorAll<HTMLInputElement>(".leadsCheckBox");

    if (!checkAll) return;

    // Count checked boxes
    const checkedCount = Array.from(elements).filter((el) => el.checked).length;

    // Update "Select All" checkbox
    checkAll.checked = checkedCount === elements.length;

    // Toggle multi-delete button
    setIsMultiDeleteButton(checkedCount > 0);
  }, []);

  const highlightMatch = (text?: string | JSX.Element, query?: string) => {
    if (!query) return text;
    if (!text) return null;

    const extractText = (element: JSX.Element | string | ReactNode): string => {

      if (typeof element === "string") {
        return element;
      }

      if (React.isValidElement(element)) {
        if (element.props['data-no-highlight']) return "";
        return React.Children.toArray(element.props.children)
          .map((child) => extractText(child))
          .join(" ");
      }


      return "";
    };

    // Extract text content from the JSX element.
    const extractedText = extractText(text);

    // If no extracted text, return as is.
    if (!extractedText) return text;


    const regex = new RegExp(`(${query})`, "gi");
    const parts = extractedText.split(regex);

    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <span key={i} style={{ fontWeight: 600 }}>
              {part}
            </span>
          ) : (
            part
          )
        )}
      </>
    );
  };

 
  const columns = useMemo(() => {


 const baseColumns = [
      ...(!viewOnlyAccess && activeTab !== "1"
        ? [
          {
            header: (
              <input
                type="checkbox"
                className="form-check-input"
                id="checkBoxAll"
                onClick={() => {
                  checkedAll()
                  setSelectedCheckBox(true)
                }
                }
              />
            ),
            cell: (cell: any) => {
              const rowData = cell.row.original;

              return (
                <input
                  type="checkbox"
                  className="leadsCheckBox form-check-input"
                  id="checkedBox"
                  value={rowData.id}
                  data-id={rowData.id}
                  onClick={() => {
                    setSelectedUserStatus(rowData.status)
                    singleCheck()
                  }
                  }
                />
              );
            },
            id: "#",
            enableSorting: false,
          },
        ]
        : []),
      
      {
        header: "S. No.",
        accessorKey: "first_name",
        enableColumnFilter: false,
        cell: (cell: any, index: number) => {
        const serialNumber = (page-1)*limit + cell.row.index + 1
          return serialNumber;
        }
      },

      {
        header: "Name",
        accessorKey: "first_name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          const fullName = `${row.first_name || ""} ${row.middle_name || ""} ${row.last_name || ""
            }`.trim();
          const userCode = row.usercode || "N/A";

          const capitalize = (str: string = "") =>
            str
              .split(" ")
              .map(
                (word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              )
              .join(" ");

          const formattedName = (
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                data-no-highlight
                className="avatar-title border rounded-circle bg-light text-primary d-flex justify-content-center align-items-center"
                style={{
                  width: "40px",
                  height: "40px",
                  marginRight: "10px",
                  fontSize: "12px",
                }}
              >
                {fullName ? fullName.charAt(0).toUpperCase() : "N/A"}
              </div>

              <div style={{ display: "flex", flexDirection: "column" }}>
                <div>{fullName ? capitalize(fullName) : "N/A"}</div>
                <div data-no-highlight style={{ fontSize: "0.8em", color: "gray" }}>{userCode}</div>
              </div>
            </div>
          );

          return highlightMatch(formattedName || "N/A", searchQuery);
        },
      },

      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return highlightMatch(value || "N/A", searchQuery);
        },
      },

      {
        header: "Phone Number",
        accessorKey: "phone_no",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          const dialCode = row.dial_code;
          const value = cell.getValue();
          const formatted =
            typeof value === "string" ? `(${dialCode}) ${value.toUpperCase()}` : value;
          return highlightMatch(formatted || "N/A", searchQuery);
        },
      },

      {
        header: "Address",
        accessorKey: "curr_address",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? <>{value}</> : <>N/A</>;
        },
      },

      {
        header: "Created On",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>NA</>
          );
        },
      },

      {
        header: "Updated On",
        accessorKey: "activeinactivedate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },

      // ✅ Hide Status column if tabStatus = "Active" or "Inactive"
      ...(["Active", "Inactive"].includes(tabStatus)
        ? []
        : [
          {
            header: "Status",
            accessorKey: "status",
            enableColumnFilter: false,
            cell: (cell: any) => {
              const value = cell.getValue().toLowerCase() === "deactivated" || cell.getValue().toLowerCase() === "inactive" ? "inactive" : "active";
              const capitalized =
                typeof value === "string" ? value.toUpperCase() : value;
              return highlightMatch(capitalized || "N/A", searchQuery);
            },
          },
        ]),
    ];


    // ✅ Common columns
    const commonColumns: any[] = [];

    if (!viewOnlyAccess) {
      commonColumns.push({
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const userData = cellProps.row.original;
          return (
            <ul className="list-inline hstack gap-1 mb-0">
              {permissions.includes(PermissionEnum.ADD_NEWUSER) && (
                <li className="list-inline-item" title="Edit">
                  <Link
                    className="edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUserClick(userData.id);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom text-muted"></i>
                  </Link>
                </li>
              )}

              {permissions.includes(PermissionEnum.CHANGE_STATUS_NEWUSER) && (
                <li className="list-inline-item" title="Action">
                  <Link
                    className="btn btn-icon btn-soft-danger btn-sm"
                    id="block-unblock-action"
                    to="#"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUserId(userData.id);

                      setSelectedUserStatus(userData.status);
                      setDeleteModal(true);
                    }}
                  >
                    <i className="ri-user-forbid-line"></i>{" "}
                  </Link>
                </li>
              )}
            </ul>
          );
        },
      });
    }

    return [...baseColumns, ...commonColumns];
  }, [activeTab, handleUserClick, checkedAll, searchQuery, tabStatus, page, limit]);



  document.title = "Users Report";


  const toggleTab = (tab: any, type: "all" | "Active" | "Inactive"|"Download") => {
    if (activeTab !== tab) {
      setActiveTab(tab);

      let filteredTeamMembers = UserData;

      if (type === "Active") {
        setTabStatus("Active");
      } else if (type === "Inactive") {
        setTabStatus("Inactive");
      } else if (type === "all") {
        setTabStatus("");
      }

      setPage(1)
      setFilteredUserData(filteredTeamMembers);
      setIsMultiDeleteButton(false);
    }
  };

  const handleUserDetails = useCallback((guid: string) => {
    setRowId(guid);
  }, []);

  const handleBackToList = useCallback(() => {
    setRowId(null);
  }, []);

  // If a lead is selected, show lead details
  if (rowID) {

    return (
      <div className="page-content">
        <Container fluid>
          <UserDetailpage id={rowID} singleUserData={singleUserData} onBack={handleBackToList} loading={loading} />
        </Container>
      </div>
    );
  }
  return (
    <React.Fragment>
      {/* <ToastContainer autoClose={3000} limit={1} /> */}
      <div className="page-content">
        <DeleteModal
          show={deleteModal}
          onDeleteClick={deleteCheckbox}

          onCloseClick={() => setDeleteModal(false)}
          text={selectedUserStatus?.toLowerCase() === "active" ? "Deactive" : "Activate"}
        />


        <Container fluid className="border pt-4 rounded-4  mb-3 ms-3 pe-4 bg-light">
          <BreadCrumb title="Users" pageTitle={["Report", "User"]} />
          <Row className="align-items-center g-3 mb-1 border-bottom pb-3 justify-content-between">
            {/* 🔍 Search Bar */}
            <Col md={3}>
              <div className="position-relative">
                <Input
                  type="text"
                  className="form-control border ps-5 ms-2 rounded-3"
                  value={searchQuery}
                  placeholder="Search by name, email, etc..."
                  onChange={handleSearchInputChange}
                />
                <i
                  className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-4"
                  style={{ fontSize: "1.1rem" }}
                ></i>
              </div>
            </Col>

            {/* 🧭 Filter Toggle Button */}
            {/* <Col md="auto">
    <button
      onClick={toggleInfo}
      type="button"
      className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
    >
      <i className="ri-filter-2-line fs-16"></i> 
      <span>Filters</span>
    </button>
  </Col>   */}



            {/* <Col md={1}> */}
            {/* <label className="form-label fw-semibold "> */}
            {/* OR    */}
            {/* </label> */}
            {/* </Col> */}
            {/* <Col> */}
            {/* <Col md={4}>
        <Flatpickr
          className="form-control"
          options={{
            mode: "range",
            dateFormat: "d M, Y",
            allowInput: true,
          }}
          value={selectedDateRange}
          onClose={(selectedDates: Date[]) => setSelectedDateRange(selectedDates)}
          placeholder="Search by date range..."
        />
      </Col>
      <Col className="d-flex ">
      {/* <div>

        <button
          className="btn btn-primary btn-sm me-2 "
          onClick={handleApplyFilter}
        >
          <i className="ri-check-line me-1 "></i> 
          <span className="">
          Apply
          </span>
        </button>
      </div> */}
            {/* <div>

        <button
          className="btn btn-outline-secondary btn-sm p-2"
          onClick={handleClearFilter}
        >
          <i className="ri-close-line me-1"></i> Clear
        </button>
      </div> */}
            {/* </Col>  */}
            {/* </Col> */}
            {/* 🗑️ Bulk Delete Button */}
            {isMultiDeleteButton && permissions.includes(PermissionEnum.CHANGE_STATUS_NEWUSER) && (
              <Col md="auto">
                <button
                  type="button"
                  className="btn btn-soft-danger btn-sm d-flex align-items-center gap-2"
                  onClick={() => setDeleteModal(true)}
                >
                  <i className="ri-user-forbid-line fs-16"></i>
                  <span>{activeTab === "2" ? "InActive" : "Active"}</span>
                </button>
              </Col>
            )}
            {permissions.includes(PermissionEnum.EXCEL_EXPORT_USERLIST) && (
              <Col md="auto">
                <button
                  type="button"
                  className="btn btn-success btn-sm d-flex align-items-center gap-2"
                  onClick={handleExportUsers}
                >
                  <i className="ri-file-excel-2-line fs-16"></i>
                  <span>Export to Excel</span>
                </button>
              </Col>
            )}
          </Row>


          {/*  Filter Panel (Visible only when toggled) */}
          {/* {isInfoDetails && (
  <div className=" p-3 rounded shadow-sm mb-3 border">
    <Row className="g-3 align-items-center">
        <label className="form-label fw-semibold text-muted">
          Date Range
        </label>
      <Col md={4}>
        <Flatpickr
          className="form-control"
          options={{
            mode: "range",
            dateFormat: "d M, Y",
            allowInput: true,
          }}
          value={selectedDateRange}
          onClose={(selectedDates: Date[]) => setSelectedDateRange(selectedDates)}
          placeholder="Select date range..."
        />
      </Col>
      <Col className="d-flex ">
      <div>

        <button
          className="btn btn-primary btn-sm me-2 "
          onClick={handleApplyFilter}
        >
          <i className="ri-check-line me-1 "></i> 
          <span className="">
          Apply
          </span>
        </button>
      </div>
      <div>

        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={handleClearFilter}
        >
          <i className="ri-close-line me-1"></i> Clear
        </button>
      </div>
      </Col>

    </Row>
  </div>
)} */}

          <Row>
            <Col lg={12}>
              <Card id="leadsList">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center" >
                    <Col>
                      <Nav
                        className="nav-tabs-custom card-header-tabs border-bottom-0 "
                        role="tablist"
                      >
                        <NavItem>
                          <NavLink
                            className={classnames(
                              { active: activeTab === "1" },
                              "fw-semibold"
                            )}
                            onClick={() => {
                              toggleTab("1", "all");
                            }}
                            href="#"
                          >
                            All
                            <span className="badge bg-danger-subtle text-danger align-middle rounded-pill ms-1">
                              {CountData?.total_items || "0"}
                            </span>
                          </NavLink>
                        </NavItem>

                        <NavItem>
                          <NavLink
                            className={classnames(
                              { active: activeTab === "2" },
                              "fw-semibold"
                            )}
                            onClick={() => {
                              toggleTab("2", "Active");
                            }}
                            href="#"
                          >
                            Active                            <span className="badge bg-danger-subtle text-danger align-middle rounded-pill ms-1">
                              {CountData?.total_active || "0"}
                            </span>
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames(
                              { active: activeTab === "3" },
                              "fw-semibold"
                            )}
                            onClick={() => {
                              toggleTab("3", "Inactive");
                            }}
                            href="#"
                          >
                            InActive                            <span className="badge bg-danger-subtle text-danger align-middle rounded-pill ms-1">
                              {CountData?.total_inactive || "0"}
                            </span>
                          </NavLink>
                        </NavItem>
                      </Nav>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody className="pt-3" style={{ minHeight: "500px" }}>
                  <div className="tab-content">
                    {!loading ? (
                      filteredUserData?.length > 0 ? (
                        <TableContainer
                          columns={columns}
                          data={filteredUserData || []}
                          isGlobalFilter={false}
                          customPageSize={limit}
                          pagination={pagination}
                          divClass="table-responsive table-card"
                          tableClass="align-middle border-bottom rounded-pill"
                          theadClass="bg-primary text-white"
                          setLimit={setLimit}
                          setPage={setPage}
                          limit={limit}
                          onPageChange={handlePageChange}
                          setRowId={setRowId}
                          onRowClick={handleUserDetails}
                        // SearchPlaceholder={searchQuery}  
                        />
                      ) : (
                        <div className="text-center p-4">No records found.</div>
                      )
                    ) : (
                      <Loader />
                    )}
                  </div>

                  <Modal
                    id="showModal"
                    isOpen={modal}
                    toggle={toggle}
                    size="xl"
                    centered
                    onClosed={() => validation.resetForm()}
                  >
                    <ModalHeader className="bg-light p-3" toggle={toggle}>
                      {!!isEdit ? "Update User" : "Add User"}
                    </ModalHeader>

                    <Form onSubmit={
                      validation.handleSubmit}>

                      <ModalBody>
                        <Input type="hidden" id="id-field" />
                        <UserForm
                          validation={validation}
                          userData={singleUserData}
                        />
                      </ModalBody>
                      <ModalFooter>
                        <div className="hstack gap-2 justify-content-end">
                          {permissions.includes(PermissionEnum.ADD_NEWUSER) && (
                            <button
                              type="submit"
                              className="btn btn-primary btn-label right"
                              id="add-btn"
                            // onClick={() => validation.handleSubmit()}
                            >
                              <i className={`label-icon align-middle fs-16 ms-2 ${isEdit ? "ri-edit-2-fill" : "ri-arrow-right-circle-fill"
                                }`}></i>{" "}

                              {isEdit ? "Update" : "Submit"}
                            </button>
                          )}
                        </div>
                      </ModalFooter>
                    </Form>
                  </Modal>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>


    </React.Fragment>
  );
};

export default UserRecords;
